/**
 * A cast RECEIVER's view of its sender (castProtocol 1.1–1.4): everything the sender told us, held for the
 * render loop — the playout clock on the sender's stream, its levels (reactivity, exposure, calm), the scene
 * it mirrors and its CastSources snapshots. ALL of it is SESSION state (endSenderSession). The transport
 * (cast/transport.ts) feeds it: the worklet's 'pos' / 'gap' reports and the validated control messages
 * (cast/control.ts). The render loop reads it once per frame.
 */
import type { CastMessage, CastScene, SourceFrame } from '@syqel/engine'
import { Engine } from '@syqel/ota-client'
import { setRuntimeEntitled } from '../catalogue'
import { setSenderFavorites } from '../favorites'

// ── castProtocol 1.1 — CastSources over the cast control channel ─────────────────────────────────
// The SENDER (the phone, which already analyzes what it streams) emits its source-contract output
// as sample-anchored snapshots; the RECEIVER holds each until playout reaches `atSample`, then
// applies it as the canonical post-update() override (same seam as stems). The playout clock comes
// from the jitter-buffer worklet ('pos' reports, extrapolated between reports). Old receivers
// reject the unknown message kind harmlessly — sender and receiver ship independently.
const CAST_STALE_SEC = 0.3 // a snapshot this far behind playout is dropped, not applied
// A snapshot this far AHEAD of playout is nonsense — senders anchor to samples they have already sent,
// so anything beyond a generous jitter/latency allowance means a restarted sender or a bad clock.
// Holding one would let it sit in the queue forever; dropping it keeps the queue converging.
const CAST_FUTURE_SEC = 5
let castWireRate = 48_000 // the stream's sample rate (CastMeta carries it; the wire default is 48k)
let castReactivityG = 1 // castProtocol 1.2: the sender's reactivity, the latest one seen this session (1 until one arrives)
let castExposureG = 0 // castProtocol 1.3: the sender's output exposure (EV), the latest seen this session (0 until one arrives)
let castCalmG = false // castProtocol 1.4: the sender's "calm flashes" switch, the latest seen this session
// castProtocol 1.4 MIRRORING — the sender's scene. `sceneSeen` is the highest-seq scene of this session (the
// isNewerScene gate: a re-send on connect or a reordered frame is a no-op); `scenePending` waits for playout
// to reach its cut sample; `sceneDue` is the one in force. All three are SESSION state (castReset).
let sceneSeen: CastScene | null = null
let scenePending: CastScene | null = null
let sceneDue: CastScene | null = null
// HOW FAR AHEAD a cut may be held for is THIS receiver's own measured playout latency — not a constant.
// A sender stamps a scene with a sample it has already SENT, ahead of the scene on the same ordered socket
// (iOS / Android / desktop senders queue the scene behind the audio before it), so by the time a scene
// arrives every sample up to its cut has arrived too: the cut sits at most `fed − playout` ahead — the
// depth of this receiver's buffer, whatever that is (a 300 ms push cushion, a grown pull cushion…). A cut
// beyond the newest sample received (plus this slack for the frame in flight) can't be on this stream's
// clock — a relay that re-based the stream, a foreign clock — so it cuts on receipt instead of waiting
// on a number that means nothing here. (This was a fixed 1.5 s, which let a sender scheduling ahead of its
// own audio cut the TV early by up to that much, and refused a legitimately deeper buffer.)
const SCENE_LEAD_SLACK_SEC = 0.25
// THE PLAYOUT CLOCK IS ANCHORED TO THE AUDIO CLOCK, not to when the main thread READ the report. A 'pos'
// report waits in the port's queue while the main thread is busy (a world build stalls it for seconds on a
// phone or a weak TV); stamped with its RECEIPT time, a report from before the stall read as "now" and every
// scene cut / sources snapshot right after it was seconds stale. The worklet stamps each report with the
// output frame it was made on (`currentFrame`), and the extrapolation runs on the context's own clock, which
// keeps time through a main-thread stall.
let castPosSample = -1 // playout stream-sample at the last worklet 'pos' report (-1 = no clock yet)
let castPosFrame = 0 // the output frame (AudioContext clock) that report was made on — extrapolation base
let castPosCtx: BaseAudioContext | null = null // whose clock that frame is on
let castFedSample = -1 // the newest stream sample the worklet had received at that report
// GAPS (native-pcm-worklet): quiet runs of the PLAYED stream, measured in audio time. `castGapSeq` counts
// completed runs this session; `castGapSec` is the length of the latest.
let castGapSeq = 0
let castGapSec = 0
let castQueue: { atSample: number; sources: SourceFrame[] }[] = []
let castApplied: { atSample: number; sources: SourceFrame[]; cleared: SourceFrame[] | null } | null = null

export function castReset(): void {
  castPosSample = -1
  castPosCtx = null
  castFedSample = -1
  castGapSeq = 0
  castGapSec = 0
  castQueue = []
  castApplied = null
  castWireRate = 48_000
  castReactivityG = 1 // a new sender may predate 1.2 — neutral until it says otherwise
  castExposureG = 0 // …or 1.3
  castCalmG = false // …or 1.4
  sceneSeen = scenePending = sceneDue = null // no scene = the receiver's own Director
}

/**
 * EVERYTHING A SENDER TOLD US IS SESSION STATE — its entitlement, favorites, levels, scene and stream clock
 * belong to that connection. One reset, run when a session OPENS (a new sender announces its own, or has
 * none) and when it CLOSES (the TV returns to itself), so no piece of a departed sender outlives it.
 */
export function endSenderSession(): void {
  castReset()
  setRuntimeEntitled(false) // → watermarked until a sender says otherwise
  setSenderFavorites([])
}

/** native-pcm-worklet's ~10 Hz playout report: `at` (the stream sample at the read head) on output frame
 *  `frame` of `ctx`; `fed` = the newest stream sample received then (playout latency = fed − at). */
export function castOnPos(report: { at: number; frame: number; fed?: number }, ctx: BaseAudioContext): void {
  castPosSample = report.at
  castPosFrame = report.frame
  castPosCtx = ctx
  castFedSample = typeof report.fed === 'number' ? report.fed : -1
}

/** Stream samples played since the last report, by the audio clock (the context runs at the wire rate on
 *  every path that reports — no resampling — so one output frame plays one stream sample, ±the worklet's
 *  ≤0.3% drift steer). */
function castSinceReport(): number {
  if (!castPosCtx) return 0
  const frames = castPosCtx.currentTime * castPosCtx.sampleRate - castPosFrame
  return Math.max(0, frames) * (castWireRate / castPosCtx.sampleRate)
}

export function castOnGap(sec: number): void {
  castGapSec = sec
  castGapSeq += 1
}

/**
 * The latest QUIET RUN of the cast as played (≥ 0.25 s under −50 dBFS — native-pcm-worklet GAPS), in AUDIO
 * seconds: `seq` counts the completed runs of this session (a caller acts on a change), `sec` is its length.
 * Absolute level, not the analyzer's normalized energy (which maps any quiet passage of a song to 0), and
 * stream time, not wall time (a frozen render loop neither stretches a gap nor hides one).
 */
export function readCastGap(): { seq: number; sec: number } {
  return { seq: castGapSeq, sec: castGapSec }
}

/** castProtocol 1.3: the output exposure (EV) the cast's SENDER shows at — a receiver applies it to its
 *  VisualHost (setExposure). 0 until a 1.3 sender announces one; kept until the next; 0 again when it leaves. */
export function readCastExposure(): number {
  return castExposureG
}

/** castProtocol 1.4: the sender's "calm flashes" switch (false until a 1.4 sender says otherwise). */
export function readCastCalm(): boolean {
  return castCalmG
}

/**
 * castProtocol 1.4 MIRRORING: the sender's scene in force at THIS render frame, or null (no sender, or one
 * that never sent a scene → the receiver's own Director). A scene carrying `atSample` waits until playout
 * reaches that sample, so the TV cuts on the same beat the sender did despite the jitter buffer; one without
 * it (or with no clock yet, or a cut sample that can't be on this stream's clock) applies on receipt.
 * The returned object is stable until a newer scene becomes due — a caller applies it on identity change.
 */
export function readCastScene(): CastScene | null {
  if (scenePending) {
    const at = scenePending.atSample
    const playout = castPlayoutSample()
    // held only while it is AHEAD of the playout and no further ahead than this receiver's own buffer (see
    // SCENE_LEAD_SLACK_SEC): at ≤ the newest sample received, plus the slack
    // the newest sample received, carried forward on the same clock: the buffer depth (fed − at) holds
    const fed = castFedSample >= 0 && playout != null ? castFedSample + castSinceReport() : playout
    if (at === undefined || playout == null || fed == null || at <= playout || at > fed + SCENE_LEAD_SLACK_SEC * castWireRate) {
      sceneDue = scenePending
      scenePending = null
    }
  }
  return sceneDue
}

/** castProtocol 1.2: the reactivity the cast's SENDER runs at — a receiver applies it to its own
 *  analyzer (setReactivity) so the TV responds as hard as the sender the user tuned. 1 until a 1.2
 *  sender announces one; kept until the next. */
export function readCastReactivity(): number {
  return castReactivityG
}

/** The receiver's current playout position on the sender's stream-sample clock (castProtocol),
 *  extrapolated between worklet reports on the audio clock. null until the first report of a cast session. */
export function castPlayoutSample(): number | null {
  if (castPosSample < 0) return null
  return castPosSample + castSinceReport()
}

/** A validated control-channel cast message arrived (cast/control.ts). */
export function castOnMessage(msg: CastMessage): void {
  const g = Engine.castReactivity(msg) // meta + sources carry it (senders always send it)
  if (g !== undefined) castReactivityG = g
  const ev = Engine.castExposure(msg) // 1.3, same carriers
  if (ev !== undefined) castExposureG = ev
  const calm = Engine.castCalm(msg) // 1.4, same carriers
  if (calm !== undefined) castCalmG = calm
  if (msg.k === 'scene') {
    if (Engine.isNewerScene(sceneSeen, msg)) { sceneSeen = msg; scenePending = msg } // a newer cut replaces a pending one
    return
  }
  if (msg.k === 'meta') {
    if (msg.sampleRate > 0) castWireRate = msg.sampleRate
  } else if (msg.k === 'sources') {
    castQueue.push({ atSample: msg.atSample, sources: msg.sources })
    if (castQueue.length > 64) castQueue.splice(0, castQueue.length - 64) // a sane sender never gets near this
  }
  // 'hints' (beat/downbeat scheduling) — not consumed by this receiver yet
}

/**
 * The CastSources override for THIS render frame, or undefined (→ the engine's own detection
 * holds). Semantics per the contract: hold each snapshot until playout reaches `atSample`; apply
 * its onset flags exactly ONCE (later frames re-attach it with onsets cleared) until the next
 * snapshot lands; drop snapshots that arrive ≥~300ms stale; and when the sender stops refreshing
 * (a pre-1.1 sender, or the cast ended) fall back to local detection rather than freezing.
 */
export function readCastSources(): SourceFrame[] | undefined {
  const playout = castPlayoutSample()
  if (playout == null) return undefined
  const staleSamples = CAST_STALE_SEC * castWireRate
  // Promote the newest READY snapshot (atSample ≤ playout), discarding older ready ones.
  // ⚠ SCAN the whole queue — do NOT drain from the head only. Snapshots can arrive out of order (and a
  // sender restart can put a far-FUTURE atSample at the head); head-draining let one such entry block
  // every valid snapshot behind it forever, freezing sources for the rest of the session.
  let ready: { atSample: number; sources: SourceFrame[] } | null = null
  const futureLimit = playout + CAST_FUTURE_SEC * castWireRate
  const held: { atSample: number; sources: SourceFrame[] }[] = []
  for (const s of castQueue) {
    if (s.atSample > playout) {
      if (s.atSample <= futureLimit) held.push(s) // not due yet — keep waiting for it
      continue // absurdly far ahead (sender restart / bad clock) — drop, never hold
    }
    if (playout - s.atSample > staleSamples) continue // too late to matter — drop
    if (!ready || s.atSample > ready.atSample) ready = s // newest due snapshot wins
  }
  castQueue = held
  if (ready) {
    castApplied = { ...ready, cleared: null }
    return ready.sources // onset flags fire exactly once — on this frame
  }
  if (!castApplied) return undefined
  if (playout - castApplied.atSample > staleSamples) return undefined // sender stopped emitting
  // re-attach the applied snapshot with onsets cleared until the next one arrives
  if (!castApplied.cleared) {
    castApplied.cleared = castApplied.sources.map((s) => (s.onset ? { ...s, onset: false, onsetStrength: 0 } : s))
  }
  return castApplied.cleared
}
